import { format, isBefore, isMatch, isValid, parseISO } from "date-fns";
import { serverTimestamp } from "firebase/firestore";
import { db } from "services/firebase";
import { invokeLLMPrompt } from "services/llmService";

/**
 * Reads the patient's uploaded documents and proposes structured health-record
 * values for them to approve.
 *
 * The document text never travels to the browser — it is injected server-side
 * by callLLMAPI's `includePatientDocuments` flag, so this module only ever sees
 * the model's extraction. Nothing is written until the patient approves each
 * item individually; see buildProposals() for why conflicts default to off.
 */

export const IMPORT_PROMPT_ID = "patient-record-import";

/** Cheap and adequate for structured extraction; matches the patient-chat default. */
const IMPORT_MODEL = "gpt-6-luna";

/**
 * Used only when llmGlobalPrompts/patient-record-import does not exist. Creating
 * that document in Query Manager overrides this without a code change — see
 * baseSections in functions/src/llm/callLLMAPI.js.
 */
export const FALLBACK_SYSTEM_PROMPT = `You extract structured medical record fields from a patient's own uploaded documents.

Return ONLY a JSON object, no prose and no code fences, in exactly this shape:

{
  "demographics": {
    "patientName": string|null,
    "dateOfBirth": "YYYY-MM-DD"|null,
    "sex": "male"|"female"|"other"|null,
    "weightKg": number|null,
    "sourceDocument": string|null
  },
  "allergies": [
    { "allergen": string, "type": "drug"|"food"|"environmental"|"other",
      "reaction": string, "notes": string, "sourceDocument": string }
  ],
  "conditions": [
    { "condition": string, "status": "active"|"controlled"|"resolved",
      "notes": string, "sourceDocument": string }
  ],
  "surgeries": [
    { "procedure": string, "year": string, "hospital": string,
      "notes": string, "sourceDocument": string }
  ],
  "medications": [
    { "name": string, "dosage": string, "frequency": string,
      "startDate": "YYYY-MM-DD"|null, "endDate": "YYYY-MM-DD"|null,
      "notes": string, "sourceDocument": string }
  ],
  "warnings": [string]
}

Rules:
- Use only what the documents state. Never infer, guess, or fill from general medical knowledge.
- Use null or an empty array when the documents do not state something. Do not invent placeholders.
- "sourceDocument" must be the filename the value came from, exactly as given.
- Report weight in kilograms as written in the document. Do not convert units.
- List a medication only when a document says this patient took or was prescribed it, including past courses that have ended. Skip medications that are only mentioned, recommended, considered, listed as an allergy, or taken by someone else.
- Write each medication as its own entry per course: the same drug taken over two separate periods is two entries.
- "dosage" and "frequency" are copied as written (e.g. "500 mg", "twice daily"), or "" when not stated.
- "startDate" and "endDate" only when the document gives the full day. When it gives only a month or a year, use null and put the partial date in "notes" (e.g. "Started March 2024"). Use null for "endDate" when the medication is ongoing or no end is stated.
- Prefer the most recent document when documents disagree about a current value.
- Add a warning if the documents appear to describe more than one person: compare patient name, date of birth, sex, and medical record number across documents. This matters more than any extracted field.
- Add a warning for any value that looks like a unit or transcription error rather than a real measurement.
- Return valid JSON and nothing else.`;

const USER_INSTRUCTION =
  "Extract the medical record fields from my uploaded documents as JSON.";

const VALID_SEX = ["male", "female", "other"];
const VALID_ALLERGY_TYPES = ["drug", "food", "environmental", "other"];
const VALID_CONDITION_STATUS = ["active", "controlled", "resolved"];

const KG_TO_LB = 2.20462;

/** Models sometimes wrap JSON in prose or fences regardless of instruction. */
export function parseExtractionResponse(raw) {
  const text = String(raw || "").trim();
  if (!text) throw new Error("The model returned an empty response.");

  const unfenced = text
    .replace(/^```(?:json)?\s*/i, "")
    .replace(/\s*```$/, "")
    .trim();

  const candidates = [unfenced];
  const firstBrace = unfenced.indexOf("{");
  const lastBrace = unfenced.lastIndexOf("}");
  if (firstBrace !== -1 && lastBrace > firstBrace) {
    candidates.push(unfenced.slice(firstBrace, lastBrace + 1));
  }

  for (const candidate of candidates) {
    try {
      const parsed = JSON.parse(candidate);
      if (parsed && typeof parsed === "object") return parsed;
    } catch {
      // try the next candidate
    }
  }

  throw new Error("Could not read the model's response as JSON.");
}

const cleanString = (value) =>
  typeof value === "string" ? value.trim() : "";

// isMatch rejects impossible days ("2024-02-30") that a regex alone lets through.
const isIsoDate = (value) =>
  /^\d{4}-\d{2}-\d{2}$/.test(value) && isMatch(value, "yyyy-MM-dd");

/** Coerce the model's output into the exact shape the health-record forms use. */
export function normalizeExtraction(parsed) {
  const raw = parsed && typeof parsed === "object" ? parsed : {};
  const rawDemo =
    raw.demographics && typeof raw.demographics === "object"
      ? raw.demographics
      : {};

  const dateOfBirth = cleanString(rawDemo.dateOfBirth);
  const sex = cleanString(rawDemo.sex).toLowerCase();
  const weightKg = Number(rawDemo.weightKg);

  const demographics = {
    patientName: cleanString(rawDemo.patientName) || null,
    dateOfBirth: isIsoDate(dateOfBirth) ? dateOfBirth : null,
    sex: VALID_SEX.includes(sex) ? sex : null,
    // The form stores pounds (see DemographicsPage "Weight (lb)") while clinical
    // documents are almost always metric, so convert at the boundary.
    weightLb:
      Number.isFinite(weightKg) && weightKg > 0 && weightKg < 500
        ? Math.round(weightKg * KG_TO_LB * 10) / 10
        : null,
    sourceDocument: cleanString(rawDemo.sourceDocument) || null,
  };

  const asArray = (value) => (Array.isArray(value) ? value : []);

  const allergies = asArray(raw.allergies)
    .map((item) => {
      const type = cleanString(item?.type).toLowerCase();
      return {
        allergen: cleanString(item?.allergen),
        type: VALID_ALLERGY_TYPES.includes(type) ? type : "other",
        reaction: cleanString(item?.reaction),
        notes: cleanString(item?.notes),
        sourceDocument: cleanString(item?.sourceDocument),
      };
    })
    .filter((item) => item.allergen);

  const conditions = asArray(raw.conditions)
    .map((item) => {
      const status = cleanString(item?.status).toLowerCase();
      return {
        condition: cleanString(item?.condition),
        status: VALID_CONDITION_STATUS.includes(status) ? status : "active",
        notes: cleanString(item?.notes),
        sourceDocument: cleanString(item?.sourceDocument),
      };
    })
    .filter((item) => item.condition);

  const surgeries = asArray(raw.surgeries)
    .map((item) => ({
      procedure: cleanString(item?.procedure),
      year: cleanString(item?.year),
      hospital: cleanString(item?.hospital),
      notes: cleanString(item?.notes),
      sourceDocument: cleanString(item?.sourceDocument),
    }))
    .filter((item) => item.procedure);

  const warnings = asArray(raw.warnings).map(cleanString).filter(Boolean);

  const medications = asArray(raw.medications)
    .map((item) => {
      const start = cleanString(item?.startDate);
      const end = cleanString(item?.endDate);
      const startDate = isIsoDate(start) ? start : null;
      let endDate = isIsoDate(end) ? end : null;
      const name = cleanString(item?.name);
      // A course that ends before it starts is a misread, and either date could
      // be the wrong one. Keep the start, drop the end, and say so.
      if (startDate && endDate && isBefore(parseISO(endDate), parseISO(startDate))) {
        warnings.push(
          `${name || "A medication"}: the end date (${endDate}) is before the start date (${startDate}), so the end date was left out.`
        );
        endDate = null;
      }
      // Only a coded source (an EHR's RxNorm coding) can supply this. A
      // document extraction never does, and a model-invented code would point
      // the dose and allergy checks at the wrong drug, so anything that isn't
      // a plain RxNorm number is dropped.
      const rxcui = cleanString(item?.rxcui);
      return {
        name,
        rxcui: /^\d+$/.test(rxcui) ? rxcui : null,
        dosage: cleanString(item?.dosage),
        frequency: cleanString(item?.frequency),
        startDate,
        endDate,
        notes: cleanString(item?.notes),
        sourceDocument: cleanString(item?.sourceDocument),
      };
    })
    .filter((item) => item.name);

  return { demographics, allergies, conditions, surgeries, medications, warnings };
}

const normalizeKey = (value) => cleanString(value).toLowerCase();

const capitalize = (value) =>
  value ? value.charAt(0).toUpperCase() + value.slice(1) : "";

const DEMOGRAPHIC_LABELS = {
  dateOfBirth: "Date of birth",
  sex: "Sex",
  weightLb: "Weight",
};

const formatDemographic = (field, value) => {
  if (value == null || value === "") return null;
  if (field === "weightLb") return `${value} lb`;
  if (field === "sex") return capitalize(value);
  if (field === "dateOfBirth") {
    const date = parseISO(String(value));
    if (isValid(date)) return format(date, "MMM d, yyyy");
  }
  return String(value);
};

/**
 * Turn an extraction into a reviewable list, diffed against what the record
 * already holds.
 *
 * `selected` is the default checkbox state, and it is deliberately conservative:
 * a proposal is pre-checked only when the field is currently empty. Anything
 * that would overwrite an existing value, or that duplicates a list entry, is
 * left for the patient to opt into. A document can belong to someone else
 * entirely, so silent overwrite is the one outcome worth designing against.
 *
 * List entries are additive only — nothing here can remove an existing entry.
 *
 * Medications live in their own users/{uid}/medications subcollection rather
 * than in healthRecords, so they are diffed against `medications`, and
 * applyProposals leaves them for saveApprovedProposals (or the caller) to add.
 */
export function buildProposals(extraction, healthRecords = {}, medications = []) {
  const hr = healthRecords || {};
  const proposals = [];

  ["dateOfBirth", "sex", "weightLb"].forEach((field) => {
    const proposed = extraction.demographics?.[field];
    if (proposed == null || proposed === "") return;

    const currentRaw = field === "weightLb" ? hr.weight : hr[field];
    const current = formatDemographic(field, currentRaw);
    const proposedLabel = formatDemographic(field, proposed);
    if (current === proposedLabel) return;

    proposals.push({
      id: `demographics.${field}`,
      group: "Demographics",
      field,
      label: DEMOGRAPHIC_LABELS[field],
      current,
      proposed: proposedLabel,
      value: proposed,
      conflict: Boolean(current),
      selected: !current,
      sourceDocument: extraction.demographics?.sourceDocument || "",
    });
  });

  const existingAllergens = new Set(
    (hr.allergies || []).map((a) => normalizeKey(a?.allergen))
  );
  extraction.allergies.forEach((item, i) => {
    const duplicate = existingAllergens.has(normalizeKey(item.allergen));
    proposals.push({
      id: `allergies.${i}`,
      group: "Allergies",
      field: "allergies",
      label: item.allergen,
      current: duplicate ? "Already in your record" : null,
      // "other" is the fallback type, not information, so it is never shown.
      proposed:
        [item.reaction, item.notes].filter(Boolean).join(" · ") ||
        (item.type !== "other" ? capitalize(item.type) : ""),
      value: {
        allergen: item.allergen,
        type: item.type,
        reaction: item.reaction,
        notes: item.notes,
      },
      conflict: duplicate,
      selected: !duplicate,
      sourceDocument: item.sourceDocument,
    });
  });

  const existingConditions = new Set(
    (hr.pmh || []).map((c) => normalizeKey(c?.condition))
  );
  extraction.conditions.forEach((item, i) => {
    const duplicate = existingConditions.has(normalizeKey(item.condition));
    proposals.push({
      id: `conditions.${i}`,
      group: "Medical conditions",
      field: "pmh",
      label: item.condition,
      current: duplicate ? "Already in your record" : null,
      // "active" is the default (a hospital record carries no status at all), so
      // only a status that says something is shown.
      proposed: [item.status !== "active" && capitalize(item.status), item.notes]
        .filter(Boolean)
        .join(" · "),
      value: {
        condition: item.condition,
        status: item.status,
        notes: item.notes,
      },
      conflict: duplicate,
      selected: !duplicate,
      sourceDocument: item.sourceDocument,
    });
  });

  const existingSurgeries = new Set(
    (hr.psh || []).map((s) => normalizeKey(s?.procedure))
  );
  extraction.surgeries.forEach((item, i) => {
    const duplicate = existingSurgeries.has(normalizeKey(item.procedure));
    proposals.push({
      id: `surgeries.${i}`,
      group: "Surgical history",
      field: "psh",
      label: item.procedure,
      current: duplicate ? "Already in your record" : null,
      proposed: [item.year, item.hospital, item.notes].filter(Boolean).join(" · "),
      value: {
        procedure: item.procedure,
        year: item.year,
        hospital: item.hospital,
        notes: item.notes,
      },
      conflict: duplicate,
      selected: !duplicate,
      sourceDocument: item.sourceDocument,
    });
  });

  // The same drug taken again later is a new course, not a duplicate, so a
  // match needs the name and the start date (both missing counts as equal).
  const courseKey = (name, startDate) => `${normalizeKey(name)}|${startDate || ""}`;
  const existingCourses = new Set(
    (medications || []).map((m) => courseKey(m?.name, m?.startDate))
  );
  (extraction.medications || []).forEach((item, i) => {
    const duplicate = existingCourses.has(courseKey(item.name, item.startDate));
    proposals.push({
      id: `medications.${i}`,
      group: "Medications",
      field: "medications",
      label: item.name,
      current: duplicate ? "Already in your record" : null,
      proposed: [
        item.dosage,
        item.frequency,
        formatCourse(item.startDate, item.endDate),
        item.notes,
      ]
        .filter(Boolean)
        .join(" · "),
      value: {
        name: item.name,
        rxcui: item.rxcui,
        dosage: item.dosage,
        frequency: item.frequency,
        startDate: item.startDate,
        endDate: item.endDate,
        notes: item.notes,
      },
      conflict: duplicate,
      selected: !duplicate,
      sourceDocument: item.sourceDocument,
    });
  });

  return proposals;
}

const formatDay = (isoDate) => format(parseISO(isoDate), "d MMM yyyy");

function formatCourse(startDate, endDate) {
  if (startDate && endDate) return `${formatDay(startDate)} – ${formatDay(endDate)}`;
  if (startDate) return `Started ${formatDay(startDate)}`;
  if (endDate) return `Until ${formatDay(endDate)}`;
  return "";
}

/**
 * The medication documents to create for the approved proposals, in the shape
 * MedicationForm saves: name, dosage and frequency always present (firestore
 * rules require the keys, blanks allowed), dates as yyyy-MM-dd or null, notes
 * omitted when empty. An rxcui only when the source supplied a coded one (an
 * EHR does; a document extraction does not), since that is what the dose and
 * allergy checks key on.
 */
export function medicationsToCreate(approved) {
  return approved
    .filter((p) => p.field === "medications")
    .map(({ value }) => ({
      name: value.name,
      dosage: value.dosage || "",
      frequency: value.frequency || "",
      startDate: value.startDate || null,
      endDate: value.endDate || null,
      ...(value.rxcui ? { rxcui: value.rxcui } : {}),
      ...(value.notes ? { notes: value.notes } : {}),
    }));
}

/**
 * A record a hospital sent through the partner API (see
 * functions/src/integrations/sharedRecords.js), in the same shape an extraction
 * has, so it goes through the same review. Weight already arrives in lb.
 */
export function partnerRecordToExtraction(record, patientName) {
  const r = record || {};
  const demo = r.demographics || {};
  const asArray = (value) => (Array.isArray(value) ? value : []);
  return {
    demographics: {
      patientName: cleanString(patientName) || null,
      dateOfBirth: isIsoDate(cleanString(demo.dateOfBirth)) ? demo.dateOfBirth : null,
      sex: VALID_SEX.includes(demo.sex) ? demo.sex : null,
      weightLb: Number.isFinite(demo.weight) && demo.weight > 0 ? demo.weight : null,
      sourceDocument: null,
    },
    allergies: asArray(r.allergies)
      .map((a) => ({
        allergen: cleanString(a?.allergen),
        // The API carries no allergy type, and guessing "drug" would be inventing it.
        type: "other",
        reaction: cleanString(a?.reaction),
        notes: cleanString(a?.notes),
        sourceDocument: "",
      }))
      .filter((a) => a.allergen),
    conditions: asArray(r.conditions)
      .map((c) => ({
        condition: cleanString(c?.condition),
        status: "active",
        notes: cleanString(c?.notes),
        sourceDocument: "",
      }))
      .filter((c) => c.condition),
    surgeries: asArray(r.surgical)
      .map((s) => ({
        procedure: cleanString(s?.procedure),
        year: cleanString(s?.year),
        hospital: "",
        notes: cleanString(s?.notes),
        sourceDocument: "",
      }))
      .filter((s) => s.procedure),
    medications: asArray(r.medications)
      .map((m) => ({
        name: cleanString(m?.name),
        dosage: cleanString(m?.dosage),
        frequency: cleanString(m?.frequency),
      }))
      .filter((m) => m.name),
    warnings: [],
  };
}

/**
 * Merge the approved proposals into a healthRecords object, matching the
 * read-merge-write shape the individual form pages use so the two paths stay
 * interchangeable.
 */
export function applyProposals(approved, healthRecords = {}) {
  const hr = healthRecords || {};
  const merged = { ...hr };
  const today = new Date().toISOString().split("T")[0];

  const appendTo = (key, entries) => {
    if (!entries.length) return;
    merged[key] = [...(hr[key] || []), ...entries];
  };

  approved.forEach((proposal) => {
    if (proposal.field === "weightLb") {
      merged.weight = proposal.value;
      merged.weightDate = today;
    } else if (proposal.group === "Demographics") {
      merged[proposal.field] = proposal.value;
    }
  });

  appendTo(
    "allergies",
    approved.filter((p) => p.field === "allergies").map((p) => p.value)
  );
  appendTo(
    "pmh",
    approved.filter((p) => p.field === "pmh").map((p) => p.value)
  );
  appendTo(
    "psh",
    approved.filter((p) => p.field === "psh").map((p) => p.value)
  );

  merged.updatedAt = new Date().toISOString();
  return merged;
}

/**
 * Which documents the check thinks may belong to someone else. A high-severity
 * identity issue naming one document is a confident outlier; naming several
 * means the checker saw a mismatch but could not say which side is wrong, so
 * nothing can be trusted for the record.
 *
 * @param {{ issues?: Array, documents?: Array<{docId, id, fileName}> }} check
 *   The consistency callable's result.
 */
export function findForeignDocuments(check) {
  const byLabel = new Map((check?.documents || []).map((d) => [d.docId, d]));
  const flagged = (check?.issues || []).filter(
    (issue) => issue.check === "identity" && issue.severity === "high"
  );
  const ambiguous = flagged.some((issue) => (issue.docIds || []).length > 1);
  const foreign = flagged
    .filter((issue) => (issue.docIds || []).length === 1)
    .map((issue) => byLabel.get(issue.docIds[0]))
    .filter(Boolean);
  return {
    ambiguous,
    docIds: foreign.map((d) => d.id),
    fileNames: foreign.map((d) => d.fileName),
  };
}

/**
 * Run the extraction. Costs one LLM call over the patient's document text.
 *
 * `exclude` names documents the consistency check flagged as possibly someone
 * else's: their ids keep them out of the text the model reads, and their file
 * names drop any proposal that still cites them (for a server that predates
 * excludeDocIds).
 *
 * @param {Object} healthRecords
 * @param {{ docIds?: string[], fileNames?: string[] }} [exclude]
 * @param {Array} [medications] the patient's current list, to mark courses already on it
 * @returns {Promise<{extraction: Object, proposals: Array}>}
 */
export async function extractFromDocuments(healthRecords, exclude = {}, medications = []) {
  const excludedNames = new Set(exclude.fileNames || []);
  const result = await invokeLLMPrompt({
    promptId: IMPORT_PROMPT_ID,
    userMessage: USER_INSTRUCTION,
    systemPrompt: FALLBACK_SYSTEM_PROMPT,
    // maxTokens has to cover the whole JSON object: a truncated response is
    // unparseable, not partially useful. Medication courses are the longest
    // list in it, so this leaves room for a long history.
    config: { model: IMPORT_MODEL, temperature: 0, maxTokens: 8000 },
    includePatientDocuments: true,
    // Read every document at full length. The per-document "use in chat"
    // toggle governs conversations, not an import the patient just asked for,
    // and the per-message chat budget would silently drop later documents.
    patientDocumentsOptions: {
      includeExcluded: true,
      maxChars: 60000,
      excludeDocIds: exclude.docIds || [],
    },
  });

  const extraction = normalizeExtraction(parseExtractionResponse(result.output));
  const proposals = buildProposals(extraction, healthRecords, medications).filter(
    (p) => !excludedNames.has(p.sourceDocument)
  );
  return { extraction, proposals };
}

/**
 * Writes what the patient approved, wherever it belongs: record fields merge
 * into users/{uid}.healthRecords, medications become documents in that user's
 * medications subcollection.
 *
 * One batch, so a failure leaves nothing half-written — retrying a partial
 * import would duplicate whichever medications had already gone through.
 * Returns the merged healthRecords so the caller can keep its copy current.
 */
export async function saveApprovedProposals({ userId, approved, healthRecords = {} }) {
  const userRef = db.collection("users").doc(userId);
  const batch = db.batch();

  const recordProposals = approved.filter((p) => p.field !== "medications");
  let merged = healthRecords;
  if (recordProposals.length) {
    merged = applyProposals(recordProposals, healthRecords);
    batch.set(userRef, { healthRecords: merged }, { merge: true });
  }

  medicationsToCreate(approved).forEach((medication) => {
    batch.set(userRef.collection("medications").doc(), {
      ...medication,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  });

  await batch.commit();
  return merged;
}
